import compareDocuments, { type CompareOptions } from "@ext/git/core/Diff/logic/compareDocuments";
import deletedTabEdits from "@ext/git/core/Diff/logic/deletedTabEdits";
import normalizeForDiff from "@ext/git/core/Diff/logic/normalizeForDiff";
import { getCachedSetting } from "@ext/settings/logic/cachedSettingsStore";
import type { DiffChange } from "@gaurussel/tiptap-diff-utility";
import type { Node, Schema } from "prosemirror-model";

// Comments have their own diff marker; as content they would mark every commented paragraph as changed.
export const IGNORED_MARKS = ["comment"];
// A tab holds only paragraphs, so the engine would otherwise compare it as one block.
export const DIFF_CONTAINERS = ["tab"];

export const resolveCompareOptions = (): CompareOptions => ({
	sensitivity: getCachedSetting("contentCompare.sensitivity"),
	minSimilarity: getCachedSetting("contentCompare.minSimilarity"),
	minMatchLength: getCachedSetting("contentCompare.minMatchLength"),
	stopWordMinBlocks: getCachedSetting("contentCompare.stopWordMinBlocks"),
	cutDownMinWords: getCachedSetting("contentCompare.cutDownMinWords"),
	cutDownMinKept: getCachedSetting("contentCompare.cutDownMinKept"),
	ignoreMarks: IGNORED_MARKS,
	containers: DIFF_CONTAINERS,
	normalize: normalizeForDiff,
});

const keyOf = (options: CompareOptions) =>
	[
		options.sensitivity,
		options.minSimilarity,
		options.minMatchLength,
		options.stopWordMinBlocks,
		options.cutDownMinWords,
		options.cutDownMinKept,
	].join("|");

export type CanonicalCache = { before: Node; after: Node; key: string; changes: DiffChange[] } | null;

export const createCanonicalChanges = (compare: typeof compareDocuments = compareDocuments) => {
	let cache: CanonicalCache = null;

	const remember = (before: Node, after: Node, key: string, changes: DiffChange[]) => {
		cache = { before, after, key, changes };
		return changes;
	};

	return {
		// `libraryChanges` must be `editor.storage.diff.changes` computed for exactly `before` and `after`.
		adopt: (before: Node, after: Node, options: CompareOptions, libraryChanges: DiffChange[]): DiffChange[] =>
			remember(before, after, keyOf(options), deletedTabEdits(libraryChanges, before, after)),

		resolve: (before: Node, after: Node, schema: Schema, options: CompareOptions): DiffChange[] => {
			const key = keyOf(options);
			if (cache && cache.before === before && cache.after === after && cache.key === key) return cache.changes;
			return remember(before, after, key, compare(before, after, schema, options));
		},
	};
};
